import { apiClient } from '../../../lib/api/client';
import {
  contactTypeToApiQueryValue,
  normalizeContactType,
  readContactTypeField,
} from '../contactType';
import type {
  BulkCreateFollowUpPayload,
  BulkCreateFollowUpResponse,
  BulkFilteredFollowUpPayload,
  BulkFilteredFollowUpResponse,
  BulkFollowUpCountResponse,
  BulkFollowUpEligibilityResponse,
  ContactDetail,
  ContactFormValues,
  ContactKundali,
  ContactCategory,
  ContactLeadsSummary,
  ContactListItem,
  ContactSearchResult,
  ExportContactsParams,
  GetContactsParams,
  PagedResult,
} from '../types';

function toContactPayload(data: ContactFormValues) {
  return {
    categoryId: resolveContactCategoryId(data.categoryId),
    contactType: normalizeContactType(data.contactType),
    role: data.role ?? null,
    name: data.name ?? null,
    companyName: data.companyName ?? null,
    jobTitle: data.jobTitle ?? null,
    mobileNumber: data.mobileNumber ?? null,
    email: data.email ?? null,
    whatsAppNumber: data.whatsAppNumber ?? null,
    website: data.website ?? null,
    linkedIn: data.linkedIn ?? null,
    address: data.address ?? null,
    googleMapsLink: data.googleMapsLink ?? null,
    ownerUserId: data.ownerUserId ?? null,
    tags: data.tags ?? [],
    notes: data.notes ?? null,
  };
}

export async function getContacts(
  params: GetContactsParams = {}
): Promise<PagedResult<ContactListItem>> {
  const {
    pageNumber = 1,
    pageSize = 10,
    search,
    phone,
    categoryId,
    contactType,
    role,
    ownerUserId,
    createdFrom,
    createdTo,
    lastActivity,
  } = params;
  const searchParams = new URLSearchParams();
  searchParams.set('pageNumber', String(pageNumber));
  searchParams.set('pageSize', String(pageSize));
  if (search !== undefined && search !== '') searchParams.set('search', search);
  if (phone !== undefined && phone !== '') searchParams.set('phone', phone);
  if (categoryId !== undefined) searchParams.set('categoryId', String(categoryId));
  if (contactType !== undefined) {
    searchParams.set('contactType', contactTypeToApiQueryValue(contactType));
  }
  if (role !== undefined) searchParams.set('role', String(role));
  if (ownerUserId !== undefined && ownerUserId !== '') searchParams.set('ownerUserId', ownerUserId);
  if (createdFrom !== undefined && createdFrom !== '') searchParams.set('createdFrom', createdFrom);
  if (createdTo !== undefined && createdTo !== '') searchParams.set('createdTo', createdTo);
  if (lastActivity !== undefined && lastActivity !== '') searchParams.set('lastActivity', lastActivity);
  if (params.segmentIds?.length) {
    for (const id of params.segmentIds) searchParams.append('segmentIds', id);
  }
  if (params.contactCategoryIds?.length) {
    for (const id of params.contactCategoryIds) searchParams.append('contactCategoryIds', String(id));
  }
  if (params.lastActivity !== undefined && params.lastActivity !== '') searchParams.set('lastActivity', params.lastActivity);
  const response = await apiClient.get<PagedResult<ContactListItem>>(
    `/contacts?${searchParams.toString()}`
  );
  return {
    ...response.data,
    items: response.data.items.map((item) => ({
      ...item,
      contactType: normalizeContactType(readContactTypeField(item)),
    })),
  };
}

export async function exportContacts(
  params: ExportContactsParams = {}
): Promise<Blob> {
  const { search, phone, categoryId, contactType, role, ownerUserId, createdFrom, createdTo, segmentIds, contactCategoryIds, lastActivity } = params;
  const searchParams = new URLSearchParams();
  if (search !== undefined && search !== '') searchParams.set('search', search);
  if (phone !== undefined && phone !== '') searchParams.set('phone', phone);
  if (categoryId !== undefined) searchParams.set('categoryId', String(categoryId));
  if (contactType !== undefined) {
    searchParams.set('contactType', contactTypeToApiQueryValue(contactType));
  }
  if (role !== undefined) searchParams.set('role', String(role));
  if (ownerUserId !== undefined && ownerUserId !== '') searchParams.set('ownerUserId', ownerUserId);
  if (createdFrom !== undefined && createdFrom !== '') searchParams.set('createdFrom', createdFrom);
  if (createdTo !== undefined && createdTo !== '') searchParams.set('createdTo', createdTo);
  if (lastActivity !== undefined && lastActivity !== '') searchParams.set('lastActivity', lastActivity);
  if (segmentIds?.length) {
    for (const id of segmentIds) searchParams.append('segmentIds', id);
  }
  if (contactCategoryIds?.length) {
    for (const id of contactCategoryIds) searchParams.append('contactCategoryIds', String(id));
  }
  if (lastActivity !== undefined && lastActivity !== '') searchParams.set('lastActivity', lastActivity);
  const response = await apiClient.get<Blob>(`/contacts/export?${searchParams.toString()}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function exportContactsPdf(
  params: ExportContactsParams = {}
): Promise<Blob> {
  const { search, phone, categoryId, contactType, role, ownerUserId, createdFrom, createdTo, segmentIds, contactCategoryIds, lastActivity } = params;
  const searchParams = new URLSearchParams();
  if (search !== undefined && search !== '') searchParams.set('search', search);
  if (phone !== undefined && phone !== '') searchParams.set('phone', phone);
  if (categoryId !== undefined) searchParams.set('categoryId', String(categoryId));
  if (contactType !== undefined) {
    searchParams.set('contactType', contactTypeToApiQueryValue(contactType));
  }
  if (role !== undefined) searchParams.set('role', String(role));
  if (ownerUserId !== undefined && ownerUserId !== '') searchParams.set('ownerUserId', ownerUserId);
  if (createdFrom !== undefined && createdFrom !== '') searchParams.set('createdFrom', createdFrom);
  if (createdTo !== undefined && createdTo !== '') searchParams.set('createdTo', createdTo);
  if (lastActivity !== undefined && lastActivity !== '') searchParams.set('lastActivity', lastActivity);
  if (segmentIds?.length) {
    for (const id of segmentIds) searchParams.append('segmentIds', id);
  }
  if (contactCategoryIds?.length) {
    for (const id of contactCategoryIds) searchParams.append('contactCategoryIds', String(id));
  }
  if (lastActivity !== undefined && lastActivity !== '') searchParams.set('lastActivity', lastActivity);
  const response = await apiClient.get<Blob>(`/contacts/export/pdf?${searchParams.toString()}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function getContactById(id: string): Promise<ContactDetail> {
  const response = await apiClient.get<ContactDetail>(`/contacts/${id}`);
  return {
    ...response.data,
    contactType: normalizeContactType(readContactTypeField(response.data)),
  };
}

function resolveContactCategoryId(categoryId: number | undefined): number {
  if (categoryId == null || categoryId <= 0) {
    throw new Error('Please select a contact category.');
  }
  return categoryId;
}

export async function createContact(data: ContactFormValues): Promise<{ id: string }> {
  const response = await apiClient.post<{ id: string }>('/contacts', toContactPayload(data));
  return response.data;
}

export async function deleteContact(id: string): Promise<void> {
  await apiClient.delete(`/contacts/${id}`);
}

export async function updateContact(id: string, data: ContactFormValues): Promise<void> {
  await apiClient.put(`/contacts/${id}`, toContactPayload(data));
}

export async function getContactLeadsSummary(id: string): Promise<ContactLeadsSummary> {
  const response = await apiClient.get<ContactLeadsSummary>(`/contacts/${id}/leads/summary`);
  return response.data;
}

export async function searchContacts(q: string): Promise<ContactSearchResult[]> {
  const response = await apiClient.get<ContactSearchResult[]>('/contacts/search', {
    params: { q },
  });
  return response.data.map((item) => ({
    ...item,
    contactType: normalizeContactType(readContactTypeField(item)),
  }));
}

export async function getContactKundali(id: string): Promise<ContactKundali> {
  const response = await apiClient.get<ContactKundali>(`/contacts/${id}/kundali`);
  return {
    ...response.data,
    contactType: normalizeContactType(readContactTypeField(response.data)),
  };
}

export interface ContactRoleOption {
  value: number;
  label: string;
}

export async function getContactRoles(): Promise<ContactRoleOption[]> {
  const response = await apiClient.get<ContactRoleOption[]>('/metadata/contact-roles');
  return response.data;
}

export async function getContactCategories(): Promise<ContactCategory[]> {
  const response = await apiClient.get<ContactCategory[]>('/metadata/contact-categories');
  return response.data;
}

export async function bulkCreateFollowUps(
  payload: BulkCreateFollowUpPayload
): Promise<BulkCreateFollowUpResponse> {
  const response = await apiClient.post<BulkCreateFollowUpResponse>(
    '/contacts/bulk-create-follow-up',
    payload
  );
  return response.data;
}

export async function bulkCreateFilteredFollowUps(
  payload: BulkFilteredFollowUpPayload
): Promise<BulkFilteredFollowUpResponse> {
  const response = await apiClient.post<BulkFilteredFollowUpResponse>(
    '/contacts/bulk-follow-up',
    payload
  );
  return response.data;
}

export async function getBulkFollowUpCount(
  filterCriteria: BulkFilteredFollowUpPayload['filterCriteria']
): Promise<BulkFollowUpCountResponse> {
  const response = await apiClient.post<BulkFollowUpCountResponse>(
    '/contacts/bulk-follow-up/count',
    { filterCriteria }
  );
  return response.data;
}

export async function getBulkFollowUpEligibility(
  filterCriteria: BulkFilteredFollowUpPayload['filterCriteria']
): Promise<BulkFollowUpEligibilityResponse> {
  const response = await apiClient.post<BulkFollowUpEligibilityResponse>(
    '/contacts/bulk-follow-up/eligibility',
    { filterCriteria }
  );
  return response.data;
}
